import { useCallback, useMemo } from "react";
import { useApolloClient } from "@apollo/client/react";
import {
  getBulkActionsForEntity,
  createGenericBulkActionHandler,
  type BulkActionConfig,
  type BulkActionMutations,
  type BulkUpdateResult,
} from "@/lib/utils/crm/genericBulkActions";
import { useAuthedFetch } from "@/lib/hooks/use-authed-fetch";
import type { BaseEntity, EntityHooks } from "../../UniversalCRMTable/types";

/**
 * Bulk action wiring for the universal entity page.
 *
 * EntityHooks return narrow mutation shapes (e.g.
 * `(data: Partial<T> & { id: string }) => ...`), but `BulkActionMutations`
 * is generic and accepts wider `{ id: string } & Record<string, unknown>`
 * payloads synthesized at runtime. Adapt via thin wrappers — the narrowing
 * cast is the *only* place the variance gap is bridged.
 */
export function useBulkActionHandlers<T extends BaseEntity>(
  entityName: string,
  updateMutation: ReturnType<EntityHooks<T>["useUpdate"]>,
  deleteMutation: ReturnType<EntityHooks<T>["useDelete"]>
) {
  const authedFetch = useAuthedFetch();
  const apolloClient = useApolloClient();

  // Batch updater backed by POST /api/tickets/bulk — one request instead of the
  // per-item loop. Lives here (not in the plain bulk handlers) because it needs
  // the auth token + Apollo client, which are hook-only. Supplied for tickets;
  // other entities keep the generic loop until they gain a batch endpoint.
  const ticketsBulkUpdate = useCallback(
    async (ids: string[], patch: Record<string, unknown>): Promise<BulkUpdateResult> => {
      const res = await authedFetch("/api/tickets/bulk", {
        method: "POST",
        body: JSON.stringify({ ticket_ids: ids, ...patch }),
      });
      if (!res.ok) {
        const err = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(err.error ?? "Bulk update failed");
      }
      const json = (await res.json()) as {
        results?: { successful?: unknown[]; failed?: unknown[]; total?: number };
        message?: string;
      };
      // The batch call bypasses the per-item mutation's Apollo refetchQueries, so
      // refresh the ticket lists explicitly to match the single-update behavior.
      await apolloClient.refetchQueries({ include: ["GetTicketss", "GetTickets"] });
      const results = json.results ?? {};
      return {
        successful: Array.isArray(results.successful) ? results.successful.length : 0,
        failed: Array.isArray(results.failed) ? results.failed.length : 0,
        total: typeof results.total === "number" ? results.total : ids.length,
        message: json.message,
      };
    },
    [authedFetch, apolloClient]
  );

  const bulkMutations = useMemo<BulkActionMutations>(
    () => ({
      update: {
        mutateAsync: (input) =>
          updateMutation.mutateAsync(input as Partial<T> & { id: string }),
        isPending: updateMutation.isPending,
      },
      delete: deleteMutation,
      bulkUpdate: entityName === "tickets" ? ticketsBulkUpdate : undefined,
    }),
    [updateMutation, deleteMutation, entityName, ticketsBulkUpdate]
  );

  const bulkActions = useMemo(
    () => getBulkActionsForEntity(entityName, bulkMutations),
    [entityName, bulkMutations]
  );

  const handleBulkAction = createGenericBulkActionHandler(
    entityName,
    bulkActions,
    bulkMutations
  );

  return { bulkActions, handleBulkAction };
}
